import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import {
  FormContainer,
  Form,
  Fieldset,
  Legend,
  Label,
  TextInput,
  MessageBox,
  UpdateButton,
  ResetButton,
  NumberInput,
  Dropdown,
  Button,
} from '../../../components/FormElements';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import { getReturnUrl } from '../../../utils';
import { Grid, GridItem } from '../../../components/Grid';

const MoneyTemplatesUpdate = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [template, setTemplate] = useState([]);
  const [originalFormData, setOriginalFormData] = useState({});
  const { id } = useParams();

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus('');
    setResponseMessage('');
  };

  const resetForm = () => {
    const { templateName, template } = originalFormData;
    setTemplateName(templateName);
    setTemplate(template);
  };

  const readData = useCallback(() => {
    setResponseStatus('');
    setResponseMessage('');
    API.get(`/margins/money-margin-templates/${id}`)
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setTemplateName(data[0].templateName);
          setTemplate(
            typeof data[0].template === 'string'
              ? JSON.parse(data[0].template)
              : data[0].template
          );
          setOriginalFormData({
            templateName: data[0].templateName,
            template:
              typeof data[0].template === 'string'
                ? JSON.parse(data[0].template)
                : data[0].template,
          });
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      });
  }, [id]);

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const formData = {
      templateName,
      template,
    };
    API.put(`/margins/money-margin-templates/${id}`, formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setOriginalFormData(formData);
          setTimeout(() => {
            resetResponseData();
            window.location.href = getReturnUrl(PERMISSION_TYPES.UPDATE);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    readData();
  }, [readData]);

  const onAddMargin = (e) => {
    e.preventDefault();
    const newTemplate = [...template];
    newTemplate.push({
      fromAmount: '',
      toAmount: '',
      type: '',
      margin: '',
    });
    setTemplate(newTemplate);
  };

  const onRemoveMargin = (e, index) => {
    e?.preventDefault();
    const newTemplate = [...template];
    newTemplate.splice(index, 1);
    setTemplate(newTemplate);
  };

  const onChange = (index, key, value) => {
    const newTemplate = [...template];
    newTemplate[index][key] = value;
    setTemplate(newTemplate);
  };

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method="POST" action="#" onSubmit={onSubmit}>
          <Fieldset>
            <Label required>Template Name</Label>
            <TextInput
              value={templateName}
              onChange={setTemplateName}
              placeholder="Please enter template name"
              maxLength={100}
              disabled={isLoading === true}
            />
          </Fieldset>
          {template &&
            template.map(({ fromAmount, toAmount, type, margin }, index) => {
              return (
                <Fieldset forLegend key={index}>
                  <Legend>Margin {index + 1}</Legend>
                  <Grid
                    columns={
                      'calc(25% - 15px) calc(25% - 15px) calc(25% - 15px) calc(25% - 15px)'
                    }
                  >
                    <GridItem>
                      <Fieldset>
                        <Label required>From Amount</Label>
                        <NumberInput
                          value={fromAmount}
                          onChange={(value) =>
                            onChange(index, 'fromAmount', value)
                          }
                          placeholder="Please enter from amount"
                          disabled={isLoading}
                          pattern={`[0-9.]*`}
                        />
                      </Fieldset>
                    </GridItem>
                    <GridItem>
                      <Fieldset>
                        <Label required>To Amount</Label>
                        <NumberInput
                          value={toAmount}
                          onChange={(value) =>
                            onChange(index, 'toAmount', value)
                          }
                          placeholder="Please enter to amount"
                          disabled={isLoading}
                          pattern={`[0-9.]*`}
                        />
                      </Fieldset>
                    </GridItem>
                    <GridItem>
                      <Fieldset>
                        <Label required>Commission Type</Label>
                        <Dropdown
                          placeholder="Please select commission type"
                          value={type}
                          onChange={(value) => onChange(index, 'type', value)}
                          options={[
                            {
                              title: '-- SELECT COMMISSION TYPE --',
                              value: '',
                            },
                            {
                              title: 'Percentage',
                              value: 'Percentage',
                            },
                            {
                              title: 'Flat',
                              value: 'Flat',
                            },
                          ]}
                          disabled={isLoading}
                        />
                      </Fieldset>
                    </GridItem>
                    <GridItem>
                      <Fieldset>
                        <Label required>Margin</Label>
                        <NumberInput
                          value={margin}
                          onChange={(value) => onChange(index, 'margin', value)}
                          placeholder="Please enter margin"
                          disabled={isLoading}
                          pattern={`[0-9.]*`}
                        />
                      </Fieldset>
                    </GridItem>
                  </Grid>
                  <Button
                    onClick={(e) => onRemoveMargin(e, index)}
                    isDeleteButton
                  >
                    Remove Margin
                  </Button>
                </Fieldset>
              );
            })}
          <Fieldset>
            <Button onClick={onAddMargin}>Add Margin</Button>
          </Fieldset>
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <UpdateButton disabled={isLoading === true} type="submit">
              Update
            </UpdateButton>
            <ResetButton
              disabled={isLoading === true}
              type="button"
              onClick={resetForm}
            >
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(MoneyTemplatesUpdate);
